import { useRef } from 'react';
import PropTypes from 'prop-types';
import {
    DismissButton,
    FocusScope,
    mergeProps,
    OverlayContainer, useDialog,
    useMenuTrigger, useModal,
    useOverlay,
    useOverlayPosition
} from 'react-aria';
import { useMenuTriggerState } from 'react-stately';

import MenuList from 'client/components/aria/Menu/MenuList/MenuList';
import Button from 'client/components/aria/Button/Button';

import Icon from '@mdi/react';
import { mdiTriangleSmallDown, mdiTriangleSmallUp } from '@mdi/js';

import defaultAriaProps from 'client/utils/constants/ariaPropTypes';

import styles from 'client/components/aria/Popover/Popover.module.scss';

const Menu = ({ label, icon, variant, placement, ...props }) => {
    const triggerRef = useRef();
    const overlayRef = useRef();

    const state = useMenuTriggerState({});

    const { menuTriggerProps, menuProps } = useMenuTrigger({}, state, triggerRef);

    const { isOpen, close } = state;

    const { overlayProps } = useOverlay({
        onClose: close,
        isOpen: isOpen,
        shouldCloseOnBlur: true,
        isDismissable: true
    }, overlayRef);

    const { overlayProps: positionProps } = useOverlayPosition({
        targetRef: triggerRef,
        overlayRef,
        placement: placement,
        offset: 5,
        containerPadding: 0,
        isOpen: isOpen
    });

    const continuePropagation = evt => {
        if ((evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') && ('continuePropagation' in evt)) {
            evt.continuePropagation();
        } else {
            menuTriggerProps?.onKeyDown(evt);
        }
    };

    const { modalProps } = useModal();

    const { dialogProps } = useDialog({}, overlayRef);

    return (
        <div
            className={styles.container}
            data-testid={props['data-testid']}
        >
            <Button
                {...menuTriggerProps}
                ref={triggerRef}
                className={styles.button}
                variant={variant}
                icon={Boolean(icon)}
                data-testid={props['data-testid'] ? props['data-testid'] + '-button' : null}
                aria-label={props['aria-label']}
                onKeyDown={continuePropagation}
            >
                {label || icon}
                { label &&
                    <span aria-hidden="true">
                        <Icon path={isOpen ? mdiTriangleSmallUp : mdiTriangleSmallDown} size={1}/>
                    </span>
                }
            </Button>
            {state.isOpen &&
                (
                    <OverlayContainer>
                        <FocusScope contain restoreFocus autoFocus>
                            <div
                                className={[styles.popover].join(' ')}
                                {...mergeProps(overlayProps, modalProps, dialogProps, positionProps)}
                                ref={overlayRef}
                                data-testid={props['data-testid'] ? props['data-testid'] + '-overlay' : null}
                            >
                                <MenuList
                                    {...props}
                                    {...menuProps}
                                />
                                <DismissButton onDismiss={close} />
                            </div>
                        </FocusScope>
                    </OverlayContainer>
                )}
        </div>
    );
};

Menu.propTypes = {
    ...defaultAriaProps,
    className: PropTypes.string,
    onAction: PropTypes.func,
    label: PropTypes.string,
    icon: PropTypes.any,
    variant: PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'warning', 'success', 'info', 'transparent']),
    placement: PropTypes.oneOf([
        'bottom', 'bottom left', 'bottom right', 'bottom start', 'bottom end', 'top', 'top left', 'top right', 'top start', 'top end', 'left', 'left top', 'left bottom', 'start', 'start top', 'start bottom', 'right', 'right top', 'right bottom', 'end', 'end top', 'end bottom'
    ])
};

Menu.defaultProps = {
    variant: 'primary',
    placement: 'bottom left'
};

export default Menu;
